import { Link } from "@/lib/router-compat";
import { useSEO } from "@/hooks/useSEO";
import { useAuth } from "@/hooks/useAuth";
import { ArrowLeft, ArrowRight, RefreshCw, Brain, Calendar, BarChart3, CheckCircle } from "lucide-react";
import { ArticleJsonLd } from "@/components/resources/ArticleJsonLd";
import { ArticleTOC } from "@/components/resources/ArticleTOC";


const sections = [
  { id: "what-is-sr", title: "What Is Spaced Repetition?" },
  { id: "sm2-algorithm", title: "The SM-2 Algorithm Explained" },
  { id: "ai-enhancement", title: "How AI Enhances Spaced Repetition" },
  { id: "practical-workflow", title: "Practical Workflow with AI" },
  { id: "comparison", title: "AI Spaced Repetition vs Traditional" },
  { id: "key-takeaways", title: "Key Takeaways" },
];

export default function AISpacedRepetitionGuide() {
  const { user } = useAuth();
  useSEO({
    title: "AI-Powered Spaced Repetition: How It Works | System Atlas",
    description: "How AI improves spaced repetition with automatic scheduling (SM-2), quiz-driven reviews, and gap analysis. Complete guide to AI-powered long-term retention.",
    type: "article",
    alternates: [
      { hreflang: "en", href: "https://systematlas.fr/learn/ai-spaced-repetition" },
      { hreflang: "fr", href: "https://systematlas.fr/fr/learn/ai-spaced-repetition" },
      { hreflang: "x-default", href: "https://systematlas.fr/learn/ai-spaced-repetition" },
    ],
  });

  return (
    <>
      <ArticleJsonLd title="AI-Powered Spaced Repetition: How It Works" description="Complete guide to AI-enhanced spaced repetition with SM-2 algorithm and gap analysis." publishedDate="2026-03-06" modifiedDate="2026-03-06" inLanguage="en" />
      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <Link to="/learn" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"><ArrowLeft className="w-4 h-4" />Back to guides</Link>

        <article>
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-4 tracking-tight">AI-Powered Spaced Repetition: How It Works</h1>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">Spaced repetition is the most evidence-backed technique for long-term memory. AI makes it practical by automating the scheduling, generating review content, and identifying what you actually need to review.</p>

          <ArticleTOC sections={sections} />

          <section id="what-is-sr" className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2"><Brain className="w-5 h-5 text-primary" />What Is Spaced Repetition?</h2>
            <p className="text-muted-foreground mb-4">Spaced repetition is a learning technique where you review material at <strong>increasing intervals</strong> over time. Instead of cramming everything the night before, you review each concept just before you're about to forget it.</p>
            <p className="text-muted-foreground mb-4">The science is clear: spacing your reviews dramatically improves long-term retention compared to massed practice (cramming). This is based on the <strong>forgetting curve</strong> discovered by Hermann Ebbinghaus in the 1880s.</p>
            <div className="p-4 bg-muted/30 border border-border/60 rounded-lg text-sm text-muted-foreground">
              <strong>Example intervals:</strong> Review after 1 day → 3 days → 7 days → 14 days → 30 days. If you get it wrong, the interval resets to shorter.
            </div>
          </section>

          <section id="sm2-algorithm" className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2"><RefreshCw className="w-5 h-5 text-primary" />The SM-2 Algorithm Explained</h2>
            <p className="text-muted-foreground mb-4">SM-2 (SuperMemo 2) is the algorithm that powers most modern spaced repetition systems, including Anki and System Atlas. Here's how it works:</p>
            <ul className="space-y-2 text-muted-foreground">
              <li>• Each item has an <strong>ease factor</strong> (how easy it is for you) starting at 2.5</li>
              <li>• After each review, you rate your recall quality (0–5 scale)</li>
              <li>• <strong>Good recall</strong> → interval increases, ease factor adjusts up</li>
              <li>• <strong>Poor recall</strong> → interval resets to 1 day, ease factor adjusts down</li>
              <li>• The algorithm calculates the <strong>optimal next review date</strong> for each item</li>
            </ul>
            <p className="text-muted-foreground mt-4 text-sm">The result: you spend more time on difficult material and less time on things you already know well.</p>
          </section>

          <section id="ai-enhancement" className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-primary" />How AI Enhances Spaced Repetition</h2>
            <p className="text-muted-foreground mb-4">Traditional spaced repetition requires you to create your own cards. AI removes this bottleneck:</p>
            <div className="space-y-3">
              <div className="p-4 border border-border/60 rounded-lg">
                <h3 className="font-medium mb-1">Automatic content generation</h3>
                <p className="text-sm text-muted-foreground">Upload a PDF → get a revision sheet + quiz. No manual card creation needed.</p>
              </div>
              <div className="p-4 border border-border/60 rounded-lg">
                <h3 className="font-medium mb-1">Quiz-driven scheduling</h3>
                <p className="text-sm text-muted-foreground">Your quiz scores feed directly into SM-2. Sheets you score well on get longer intervals; weak ones come back sooner.</p>
              </div>
              <div className="p-4 border border-border/60 rounded-lg">
                <h3 className="font-medium mb-1">Gap analysis integration</h3>
                <p className="text-sm text-muted-foreground">After each quiz, AI identifies which specific concepts to focus on. not just "review this sheet" but "review section 3.2 on X."</p>
              </div>
              <div className="p-4 border border-border/60 rounded-lg">
                <h3 className="font-medium mb-1">Study plan generation</h3>
                <p className="text-sm text-muted-foreground">A daily study plan shows exactly which sheets are due for review and which need priority attention.</p>
              </div>
            </div>
          </section>

          <section id="practical-workflow" className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2"><Calendar className="w-5 h-5 text-primary" />Practical Workflow with AI</h2>
            <ol className="space-y-3 text-muted-foreground list-decimal list-inside">
              <li><strong>Upload your course material</strong> and generate revision sheets</li>
              <li><strong>Take the first quiz</strong>. this sets your baseline for each concept</li>
              <li><strong>Check your study plan</strong> daily. it shows what's due for review</li>
              <li><strong>Review the revision sheet</strong> for due items, then retake the quiz</li>
              <li><strong>Your score updates the SM-2 schedule</strong> automatically</li>
              <li><strong>Over time</strong>, strong concepts fade to monthly reviews while weak ones get frequent attention</li>
            </ol>
          </section>

          <section id="comparison" className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">AI Spaced Repetition vs Traditional</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-border/60 rounded-lg">
                <thead><tr className="bg-muted/30"><th className="text-left p-3 font-medium">Aspect</th><th className="text-left p-3 font-medium">Traditional (Anki-style)</th><th className="text-left p-3 font-medium">AI-Powered (Atlas)</th></tr></thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-t border-border/40"><td className="p-3">Content creation</td><td className="p-3">Manual flashcard writing</td><td className="p-3">Auto-generated from PDFs</td></tr>
                  <tr className="border-t border-border/40"><td className="p-3">Review format</td><td className="p-3">Individual flashcards</td><td className="p-3">Structured sheets + MCQ quizzes</td></tr>
                  <tr className="border-t border-border/40"><td className="p-3">Scheduling</td><td className="p-3">Per-card SM-2</td><td className="p-3">Per-sheet SM-2 with gap analysis</td></tr>
                  <tr className="border-t border-border/40"><td className="p-3">Setup time</td><td className="p-3">Hours</td><td className="p-3">Seconds</td></tr>
                  <tr className="border-t border-border/40"><td className="p-3">Weak area detection</td><td className="p-3">Implicit (card fail rate)</td><td className="p-3">Explicit (AI gap analysis)</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section id="key-takeaways" className="mb-10 p-6 bg-muted/30 border border-border/60 rounded-xl">
            <h2 className="text-xl font-semibold mb-3">Key Takeaways</h2>
            <ul className="space-y-2 text-muted-foreground text-sm">
              <li>• Spaced repetition is the most evidence-backed method for long-term memory</li>
              <li>• SM-2 calculates optimal review intervals based on your performance</li>
              <li>• AI removes the biggest barrier (manual card creation) while adding gap analysis</li>
              <li>• The combination of auto-generated quizzes + SM-2 scheduling + gap analysis creates a complete retention system</li>
            </ul>
          </section>
        </article>

        {!user && (
          <section className="text-center p-8 bg-primary/5 border border-primary/15 rounded-xl mb-10">
            <h2 className="text-lg font-medium mb-2">Start Using AI Spaced Repetition</h2>
            <p className="text-sm text-muted-foreground mb-6">Upload a PDF and get your first AI-scheduled review plan. free.</p>
            <Link to="/"><button className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">Get Started Free <ArrowRight className="w-4 h-4" /></button></Link>
          </section>
        )}

        <nav className="flex flex-col gap-2 text-sm">
          <span className="text-muted-foreground font-medium">Related guides:</span>
          <Link to="/learn/spaced-repetition" className="text-primary hover:underline">Spaced Repetition: The Science of Long-Term Memory →</Link>
          <Link to="/learn/active-recall" className="text-primary hover:underline">Active Recall: The Science of Effective Learning →</Link>
          <Link to="/learn/ai-quiz-generator" className="text-primary hover:underline">How to Generate Quizzes from Your Notes with AI →</Link>
        </nav>
      </div>
    </>
  );
}
